import React, { FC, MouseEvent, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DownloadIconCont,
  NoReport,
  ReportGroup,
  ReportsNestedTableTileCont,
  ReportsNestedTableTileContentCont,
  ReportsRegularNestedTableTileEl,
  ReportsLowercaseNestedTableTileEl,
  ReportTileCont,
  TooltipContainer,
  TooltipList,
  TooltipContent,
  ResizeBlock,
  SubReportsContainer,
} from './ReportsNestedTableTile.styles';
import { RouteNames, RoleNames } from '../../../../constans/routes';
import Tooltip from '../../../../HOC/Tooltip/Tooltip';
import { useAuth } from '../../../../hooks/useAuth';
import { useReports } from '../../../../hooks/useReports';
import { IItemReportResponse } from '../../../../services/reports';
import { IShareUser } from '../../../../services/surveys';
import DownloadReportModal from '../../../DownloadReportModal/DownloadReportModal';
import ResponseRate from '../../../ResponseRate/ResponseRate';
import CloseAll from '@/assets/icons/closeAllAccordion.svg?react';
import DownloadIcon from '@/assets/icons/download.svg?react';
import OpenAll from '@/assets/icons/openAllAccordion.svg?react';

interface IReportsNestedTableTile {
  item: IItemReportResponse;
  surveyType: string;
  depth: number;
  isLastChild?: boolean;
  isVisualLastChild?: boolean;
  isExpanded?: boolean;
}
const ReportsNestedTableTile: FC<IReportsNestedTableTile> = ({
  item,
  surveyType,
  depth,
  isLastChild,
  isVisualLastChild = false,
}) => {
  const [active, setActive] = useState<boolean>(false);
  const { t } = useTranslation();
  const { id, group, type, responsiveness, accessList, is_anonymized, sub_reports, department } = item;
  const [downloadIconVis, setDownloadIconVis] = useState(false);
  const { getReportType } = useReports();
  const { userRolesFromToken, isEBHRMember } = useAuth();
  const [disableRowHover, setDisableRowHover] = useState(false);

  const users = accessList?.users ?? [];

  const accessUsersString = useMemo(
    () => users.map(u => u?.name || u?.email || ' - ').join(', '),
    [users]
  );

  const accessUsersList = useMemo(
    () => users.map(u => <div key={u?.name || u?.email}>{u?.name || u?.email || ' - '}</div>),
    [users]
  );

  const showAccessTooltip = users.length > 3;

  const handleRedirectPrevent = (event: MouseEvent<HTMLAnchorElement>) => {
    if (!is_anonymized) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  const hideAccess = !isEBHRMember && userRolesFromToken.roles?.includes(RoleNames.Manager);

  const handleShowSubReports = (e: MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setActive(active => !active);
  };
  const shouldPropagateVisualLast = depth === 0 ? true : isVisualLastChild;
  const isExpanded = Boolean(active && sub_reports?.length);

  return (
    <>
      <ReportTileCont
        onMouseOver={() => setDownloadIconVis(true)}
        onMouseLeave={() => setDownloadIconVis(false)}
      >
        <ReportsNestedTableTileCont
          id="ReportsNestedTableTileCont"
          onClick={handleRedirectPrevent}
          to={`${RouteNames.Reports}/${department}?surveyType=${surveyType}`}
          is_anonymized={is_anonymized}
          depth={depth}
          isLastChild={isLastChild}
          isVisualLastChild={isVisualLastChild}
          isExpanded={isExpanded}
          isActive={active}
          disableHover={disableRowHover}
        >
          <ReportsNestedTableTileContentCont id="ReportsNestedTableTileContentCont">
            {sub_reports?.length ? (
              <ResizeBlock
                onMouseEnter={() => setDisableRowHover(true)}
                onMouseLeave={() => setDisableRowHover(false)}
                onClick={handleShowSubReports}
              >
                {active ? <CloseAll /> : <OpenAll />}
              </ResizeBlock>
            ) : (
              <ResizeBlock />
            )}
            <ReportGroup id="ReportGroup">{group}</ReportGroup>
            <ReportsRegularNestedTableTileEl id="ReportsRegularNestedTableTileEl">
              {getReportType(type)}
            </ReportsRegularNestedTableTileEl>
            <ReportsLowercaseNestedTableTileEl id="ReportsLowercaseNestedTableTileEl">
              {!hideAccess && type === 'executive' ? (
                showAccessTooltip ? (
                  <TooltipContainer id="TooltipCont">
                    <Tooltip
                      tooltipDesc={<TooltipList>{accessUsersList}</TooltipList>}
                      positionY="110%"
                      arrowPositionX="50%"
                      position="top"
                      width="fit-content"
                      background={is_anonymized ? 'rgba(0, 0, 0, 0.7)' : 'rgba(0, 0, 0, 1)'}
                    >
                      <TooltipContent>
                        {users
                          .slice(0, 5)
                          .map(u => u?.name || u?.email || ' - ')
                          .join(', ')}
                        ...
                      </TooltipContent>
                    </Tooltip>
                  </TooltipContainer>
                ) : (
                  accessUsersString
                )
              ) : (
                // 👇 invisible placeholder keeps layout
                <span style={{ visibility: 'hidden' }}>—</span>
              )}
            </ReportsLowercaseNestedTableTileEl>
            <ReportsRegularNestedTableTileEl id="ReportsRegularNestedTableTileEl">
              <ResponseRate
                mode={responsiveness.representativeness}
                percent={responsiveness.responseRate}
                responseCount={responsiveness.responseCount}
                maxResponseCount={responsiveness.participantCount}
              />
            </ReportsRegularNestedTableTileEl>
          </ReportsNestedTableTileContentCont>
        </ReportsNestedTableTileCont>
        {is_anonymized ? (
          <DownloadReportModal
            metadata={item}
            customButtonVisible={downloadIconVis}
            setCustomButtonVisible={setDownloadIconVis}
            customActionButton={
              <DownloadIconCont id="DownloadButton">
                <DownloadIcon />
              </DownloadIconCont>
            }
            customReportId={id}
          />
        ) : (
          <NoReport>{t('reports.noReportAvailable')}</NoReport>
        )}
      </ReportTileCont>
      {active && sub_reports?.length ? (
        <SubReportsContainer $depth={depth}>
          {sub_reports.map((subReport, index) => (
            <ReportsNestedTableTile
              key={subReport.id}
              item={subReport}
              surveyType={surveyType}
              depth={depth + 1}
              isLastChild={index === sub_reports.length - 1}
              isVisualLastChild={
                shouldPropagateVisualLast && index === sub_reports.length - 1
              }
            />
          ))}
        </SubReportsContainer>
      ) : null}
    </>
  );
};

export default ReportsNestedTableTile;
